// App.tsx
import { Routes, Route, useNavigationType, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import ErrolPage from './pages/enroll/EnrollPage';
import Backoffice from 'pages/backoffice/Backoffice';
import { Policies } from 'pages/policies/Policies';
import { Terms } from 'pages/terms/terms';
import { ForgotPasswordPage } from 'pages/fogot-password/ForgotPassword';
import { BackofficeTermsPolicies } from 'pages/backoffice-terms-policies/BackofficeTermsPolicies';
import { RouteGuard } from 'RouteGuard';
import GrapesJSEditor from 'pages/editor-backoffice/EditoBackoffice';
import PageViewer from 'pages/iframe-landing/iframeLanding';
import UsersComponent from 'pages/users/users';
import RegisterUserComponent from 'pages/users/register-user';
import UserSessions from 'pages/usser-sessions/UserSessions';
import SubcriptionsManagement from 'pages/subcriptionsManagement/subcriptionsManagement';
import SubscriptionPlans from 'pages/subscription/subscription';
import SubscriptionInvoices from 'pages/subscription-invoice/SusbcriptionInvoice';
import SubscriptionsSubmerchant from 'pages/subcriptionsManagement/subscriptionsSubmerchant';
import ReportSettings from 'pages/reports-master/ReportsMaster';
import ClientPortal from 'pages/client-portal/ClientPortal';
import LawyerDirectory from 'pages/directory/DirectoryMaster';
import LawyerSearchLanding from 'pages/directory/LawyerSearchLanding';
import LawyerProfile from 'pages/directory/LawyerProfile';
import DirectorySettings from 'pages/directory/DirectorySettings';
import SeoSettings from 'pages/seo-settings/SeoSettings';
import { newServer } from '../src/constants/constants';

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;

  // Al cambiar de ruta (a menos que sea POP) hacemos scroll al inicio
  useEffect(() => {
    if (action !== 'POP') {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  // Función para obtener los settings SEO desde el backend
  const getSettings = async () => {
    const response = await fetch(`${newServer}seo-settings/seo`, {
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error(`Error fetching SEO settings: ${response.status}`);
    }
    return await response.json();
  };

  // Efecto para actualizar las meta etiquetas, hreflang y el marcado estructurado (JSON-LD)
  useEffect(() => {
    const updateMetaTags = async () => {
      try {
        const settings = await getSettings();
        console.log('SEO settings:', settings);

        // Variables por defecto – se pueden extender para cada ruta
        let title = '';
        let metaDescription = '';
        let metaKeywords = '';
        let metaImage = '';
        let structuredData: any = null;

        switch (pathname) {
          case '/':
            title = settings[0]?.title || 'Law Firms - Home';
            metaDescription =
              settings[0]?.description ||
              'Enhance fund management, coordinate events, strengthen client collaboration, and promote your services.';
            metaKeywords = settings[0]?.keywords || 'law firms, legal services, fund management';
            metaImage = settings[0]?.image || 'https://paymylawyer.cc/og-image.jpg';
            structuredData = {
              '@context': 'https://schema.org',
              '@type': 'LegalService',
              name: title,
              address: {
                '@type': 'PostalAddress',
                streetAddress: '123 Main Street',
                addressLocality: 'City',
                addressCountry: 'US',
              },
              areaServed: ['US'],
              legalName: title,
              sameAs: ['https://paymylawyer.cc'],
            };
            break;
          default:
            title = settings[0]?.title || 'Law Firms';
            metaDescription = settings[0]?.description || 'Legal services platform';
            metaKeywords = settings[0]?.keywords || 'legal, law, services';
            metaImage = settings[0]?.image || 'https://paymylawyer.cc/og-image.jpg';
            structuredData = {
              '@context': 'https://schema.org',
              '@type': 'LegalService',
              name: title,
              address: {
                '@type': 'PostalAddress',
                streetAddress: '123 Main Street',
                addressLocality: 'City',
                addressCountry: 'US',
              },
              areaServed: ['US'],
              legalName: title,
              sameAs: ['https://paymylawyer.cc'],
            };
        }

        // Actualizamos el título del documento
        document.title = title;

        // Actualizamos meta description
        let metaDescriptionTag = document.querySelector('head > meta[name="description"]');
        if (metaDescriptionTag) {
          metaDescriptionTag.setAttribute('content', metaDescription);
        } else {
          metaDescriptionTag = document.createElement('meta');
          metaDescriptionTag.setAttribute('name', 'description');
          metaDescriptionTag.setAttribute('content', metaDescription);
          document.head.appendChild(metaDescriptionTag);
        }

        // Actualizamos meta keywords
        let metaKeywordsTag = document.querySelector('head > meta[name="keywords"]');
        if (metaKeywordsTag) {
          metaKeywordsTag.setAttribute('content', metaKeywords);
        } else {
          metaKeywordsTag = document.createElement('meta');
          metaKeywordsTag.setAttribute('name', 'keywords');
          metaKeywordsTag.setAttribute('content', metaKeywords);
          document.head.appendChild(metaKeywordsTag);
        }

        // Actualizamos og:image
        let metaImageTag = document.querySelector('head > meta[property="og:image"]');
        if (metaImageTag) {
          metaImageTag.setAttribute('content', metaImage);
        } else {
          metaImageTag = document.createElement('meta');
          metaImageTag.setAttribute('property', 'og:image');
          metaImageTag.setAttribute('content', metaImage);
          document.head.appendChild(metaImageTag);
        }

        // --- Inyectar Hreflang tags ---
        // Se definen los hreflang (ejemplo estático; en producción podrías derivarlos de settings)
        const currentDomain = window.location.origin;
        const currentPath = window.location.pathname;
        const hreflangTags = [
          { hreflang: 'en-us', href: `${currentDomain}${currentPath}` },
          { hreflang: 'es-mx', href: `${currentDomain}${currentPath}` },
          { hreflang: 'nl-sr', href: `${currentDomain}${currentPath}` },
          { hreflang: 'es-co', href: `${currentDomain}${currentPath}` },
        ];

        hreflangTags.forEach((tag) => {
          let link = document.querySelector(`link[rel="alternate"][hreflang="${tag.hreflang}"]`);
          if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', 'alternate');
            link.setAttribute('hreflang', tag.hreflang);
            document.head.appendChild(link);
          }
          link.setAttribute('href', tag.href);
        });

        // --- Inyectar marcado estructurado (JSON-LD) ---
        let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
        if (!structuredDataScript) {
          structuredDataScript = document.createElement('script');
          structuredDataScript.setAttribute('type', 'application/ld+json');
          document.head.appendChild(structuredDataScript);
        }
        if (structuredData) {
          structuredDataScript.innerHTML = JSON.stringify(structuredData);
        }
      } catch (error) {
        console.error('Error updating SEO settings:', error);
      }
    };

    updateMetaTags();
  }, [pathname]);

  return (
    <>
      <Routes>
        <Route path='/' element={<PageViewer pageId='1' />} />
        <Route path='/enroll' element={<ErrolPage />} />
        <Route path='/backoffice' element={<Backoffice />} />
        <Route path='/reset_password' element={<ForgotPasswordPage />} />
        <Route path='/register-user' element={<RegisterUserComponent />} />
        <Route path='/subscriptions/:id/:subdomain' element={<SubscriptionPlans />} />
        <Route path='/client-portal' element={<ClientPortal />} />
        <Route path='/directory' element={<LawyerSearchLanding />} />
        <Route path='/directory/search' element={<LawyerDirectory />} />
        <Route path='/lawyers/:uuid' element={<LawyerProfile />} />
        <Route
          path='/backoffice/seo-settings'
          element={
            <RouteGuard>
              <SeoSettings />
            </RouteGuard>
          }
        />
        <Route
          path='/backoffice/backoffice-editor'
          element={
            <RouteGuard>
              <GrapesJSEditor />
            </RouteGuard>
          }
        />
        <Route
          path='/backoffice/privacy-policy'
          element={
            <RouteGuard>
              <BackofficeTermsPolicies />
            </RouteGuard>
          }
        />
        <Route
          path='/backoffice/directory'
          element={
            <RouteGuard>
              <DirectorySettings />
            </RouteGuard>
          }
        />
        <Route
          path='/privacy-policy'
          element={
              <Policies />
         
          }
        />
        <Route
          path='/terms'
          element={
              <Terms />
          }
        />
        <Route
          path='/backoffice/users'
          element={
            <RouteGuard>
              <UsersComponent />
            </RouteGuard>
          }
        />
        <Route
          path='/users-sessions'
          element={
            <RouteGuard>
              <UserSessions />
            </RouteGuard>
          }
        />
        <Route
          path='/subscriptions-management'
          element={
            <RouteGuard>
              <SubcriptionsManagement />
            </RouteGuard>
          }
        />
        <Route
          path='/subscriptions-submerchant'
          element={
            <RouteGuard>
              <SubscriptionsSubmerchant />
            </RouteGuard>
          }
        />
        <Route
          path='/subscriptions-invoice'
          element={
            <RouteGuard>
              <SubscriptionInvoices />
            </RouteGuard>
          }
        />
        <Route
          path='/reports'
          element={
            <RouteGuard>
              <ReportSettings />
            </RouteGuard>
          }
        />
      </Routes>
    </>
  );
}

export default App;
